import clsx from 'clsx';
import { FunctionComponent, MouseEvent, useCallback, useMemo } from 'react';
import { useInView } from 'react-intersection-observer';

import { ClientImageSourceSet } from '@vestico/model';

import { useImageBorderStyles } from '../hooks/useImageStyles';
import { BorderRadiusStyles } from '../model/styles';
import { ImageDisplay } from './ImageDisplay';
import style from './VideoDisplay.module.css';
import { VideoPlayer, VideoPlayerProps } from './VideoPlayer';

export interface VideoDisplayProps extends VideoPlayerProps {
  coverSrcSet?: ClientImageSourceSet;
  onClick?: (e: MouseEvent<HTMLElement>) => void;
  onMouseDown?: (e: MouseEvent<HTMLElement>) => void;
  borderRadius?: BorderRadiusStyles;
  loadInView?: boolean;
  noBackground?: boolean;
}

export const VideoDisplay: FunctionComponent<VideoDisplayProps> = ({
  url,
  coverSrcSet,
  coverURL,
  mimeType,
  onLoad,
  onClick,
  onMouseDown,
  borderRadius,
  loadInView,
  videoFit,
  cropHint,
  disableControls: disableVideoControls,
  showLoadingSpinner,
  onControlsHide,
  noBackground,
}) => {
  const { ref: inViewRef, inView } = useInView();

  const containerStyle = useImageBorderStyles(borderRadius);

  const showingCoverImageOnly = useMemo(() => !inView && loadInView, [inView, loadInView]);

  const handleImageClick = useCallback(
    (e: MouseEvent<HTMLImageElement>) => {
      if (onClick) {
        e.preventDefault();
        onClick(e);
      }
    },
    [onClick]
  );

  const handleMouseDown = useCallback(
    (e: MouseEvent<HTMLImageElement>) => {
      if (onMouseDown) {
        e.preventDefault();
        onMouseDown(e);
      }
    },
    [onMouseDown]
  );

  const mediaMarkup =
    showingCoverImageOnly && coverURL ? (
      <ImageDisplay url={coverURL} srcSet={coverSrcSet} onClick={handleImageClick} onMouseDown={handleMouseDown} cropHint={cropHint} />
    ) : (
      <VideoPlayer
        url={url}
        coverURL={coverURL}
        mimeType={mimeType}
        onLoad={onLoad}
        videoFit={videoFit}
        cropHint={cropHint}
        disableControls={disableVideoControls}
        onControlsHide={onControlsHide}
        showLoadingSpinner={showLoadingSpinner}
      />
    );
  return (
    <div className={clsx(style.root, { [style.clear]: noBackground })} ref={inViewRef} style={containerStyle}>
      {mediaMarkup}
    </div>
  );
};
